import React, { useMemo } from "react";
import { Box, CircularProgress, useMediaQuery } from "@mui/material";
import EditLocationAltIcon from "@mui/icons-material/EditLocationAlt";
import { useFormContext } from "react-hook-form";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { OfferFormData, OfferServiceRequest } from "../../models/models";
import { PublicServiceRequest } from "../../../serviceBoard/models/models";
import PricingLineItem from "../pricing/PricingLineItem";
import PricingSummary from "../pricing/PricingSummary";
import NoLocationBanner from "../pricing/NoLocationBanner";
import DefaultIndicator from "../shared/DefaultIndicator";
import { dt } from "../../../../shared/themes/redesignTokens";

interface StepPricingProps {
  serviceRequest?: OfferServiceRequest;
  fullServiceRequest?: PublicServiceRequest;
  fieldsFromDefaults?: Set<string>;
  onFieldChange?: (fieldName: string) => void;
  /** Whether the provider has a valid lat/lng (for no-location warning) */
  providerHasLocation?: boolean;
  /** Travel distance/time info source */
  travelInfoSource?: "directions" | "estimated" | "manual" | null;
  /** Whether the directions calculation is loading */
  travelCalculating?: boolean;
  /** Callback to open the DeparturePointModal */
  onChangeDeparture?: () => void;
}

const StepPricing = ({
  serviceRequest,
  fullServiceRequest,
  fieldsFromDefaults,
  onFieldChange,
  providerHasLocation = true,
  travelInfoSource,
  travelCalculating = false,
  onChangeDeparture,
}: StepPricingProps) => {
  const { translate } = useTranslation(["serviceOffers"]);
  const { watch, setValue } = useFormContext<OfferFormData>();
  const isDesktop = useMediaQuery("(min-width:1025px)");

  // --- Watch form fields ---
  const laborHours = watch("laborHours");
  const hourlyRate = watch("hourlyRate");
  const laborCost = watch("laborCost");

  const travelDistanceKm = watch("travelDistanceKm");
  const travelRatePerKm = watch("travelRatePerKm");
  const travelDistanceSlider = watch("travelDistanceSliderAdjustment") || 0;

  const travelTimeHours = watch("travelTimeHours");
  const travelRatePerHour = watch("travelRatePerHour");
  const travelTimeSlider = watch("travelTimeSliderAdjustment") || 0;

  const partsCost = watch("partsCost");
  const materialDescription = watch("materialDescription");

  const additionalCosts = watch("additionalCosts");
  const additionalCostsNote = watch("additionalCostsNote");

  // --- Computed costs ---
  const computedLaborCost = useMemo(() => {
    if (laborHours && hourlyRate) {
      return Number(laborHours) * Number(hourlyRate);
    }
    return Number(laborCost) || 0;
  }, [laborHours, hourlyRate, laborCost]);

  // Auto-update laborCost when hours or rate change
  React.useEffect(() => {
    if (laborHours && hourlyRate) {
      setValue("laborCost", Number(laborHours) * Number(hourlyRate));
    }
  }, [laborHours, hourlyRate, setValue]);

  // Travel distance computed — slider adjusts km, price is derived
  const baseDistanceKm = useMemo(() => Number(travelDistanceKm) || 0, [travelDistanceKm]);
  const distanceRatePerKm = useMemo(() => Number(travelRatePerKm) || 0, [travelRatePerKm]);
  const adjustedDistanceKm = baseDistanceKm * (1 + travelDistanceSlider / 100);
  const computedTravelDistanceCost = baseDistanceKm * distanceRatePerKm;
  const finalTravelDistanceCost = adjustedDistanceKm * distanceRatePerKm;

  // Travel time computed — slider adjusts hours, price is derived
  const baseTimeHours = useMemo(() => Number(travelTimeHours) || 0, [travelTimeHours]);
  const timeRatePerHour = useMemo(() => Number(travelRatePerHour) || 0, [travelRatePerHour]);
  const adjustedTimeHours = baseTimeHours * (1 + travelTimeSlider / 100);
  const computedTravelTimeCost = baseTimeHours * timeRatePerHour;
  const finalTravelTimeCost = adjustedTimeHours * timeRatePerHour;

  // Auto-update travelCost when travel inputs change
  React.useEffect(() => {
    const total = Math.max(0, finalTravelDistanceCost) + Math.max(0, finalTravelTimeCost);
    if (computedTravelDistanceCost > 0 || computedTravelTimeCost > 0) {
      setValue("travelCost", total);
    }
  }, [finalTravelDistanceCost, finalTravelTimeCost, computedTravelDistanceCost, computedTravelTimeCost, setValue]);

  // --- Final totals (labor, parts, additional have no slider now) ---
  const finalLabor = computedLaborCost;
  const finalTravelDistance = finalTravelDistanceCost;
  const finalTravelTime = finalTravelTimeCost;
  const finalMaterial = Number(partsCost) || 0;
  const finalAdditional = Number(additionalCosts) || 0;
  const grandTotal =
    Math.max(0, finalLabor) +
    Math.max(0, finalTravelDistance) +
    Math.max(0, finalTravelTime) +
    Math.max(0, finalMaterial) +
    Math.max(0, finalAdditional);

  const defaults = fieldsFromDefaults || new Set<string>();
  const handleFieldChange = (fieldName: string) => {
    if (onFieldChange) onFieldChange(fieldName);
  };

  // Determine which fields need manual input (not auto-filled from defaults or auto-calculated)
  // Highlight labor card when hours are not filled (user needs to provide hours manually)
  const laborNeedsManual = !laborHours && !laborCost;
  const partsNeedsManual = false; // Parts is optional — no manual highlight needed
  const additionalNeedsManual = false; // Additional is always optional

  // Travel info source badge
  const travelSourceBadge = travelCalculating ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <CircularProgress size={12} sx={{ color: dt.blue }} />
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: "11px",
          color: dt.textSecondary,
        }}
      >
        {translate("travelInfo.calculating")}
      </Box>
    </Box>
  ) : travelInfoSource === "directions" ? (
    <Box
      sx={{
        fontFamily: dt.font,
        fontSize: "11px",
        color: "#10B981",
        fontWeight: 500,
      }}
    >
      {translate("travelInfo.autoCalculated")}
    </Box>
  ) : travelInfoSource === "estimated" ? (
    <Box
      sx={{
        fontFamily: dt.font,
        fontSize: "11px",
        color: "#F59E0B",
        fontWeight: 500,
      }}
    >
      {translate("travelInfo.estimated")}
    </Box>
  ) : null;

  // "Change departure" link
  const changeDepartureLink = onChangeDeparture ? (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 0.5 }}>
      {travelSourceBadge}
      <Box
        component="button"
        type="button"
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          onChangeDeparture?.();
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          background: "none",
          border: "none",
          cursor: "pointer",
          fontFamily: dt.font,
          fontSize: "11px",
          fontWeight: 500,
          color: dt.blue,
          p: 0,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        <EditLocationAltIcon sx={{ fontSize: 14 }} />
        {translate("departure.changeDeparture")}
      </Box>
    </Box>
  ) : travelSourceBadge ? (
    <Box>{travelSourceBadge}</Box>
  ) : null;

  const pricingLineItems = (
    <>
      {/* Labor — no slider, needsManualInput if empty */}
      <Box>
        <PricingLineItem
          title={translate("laborCost")}
          inputs={[
            {
              label: translate("laborHoursInput"),
              value: laborHours,
              onChange: (v) => setValue("laborHours", v),
              placeholder: "0",
              adornment: "hrs",
              autoFocus: !isDesktop,
            },
            {
              label: translate("hourlyRateInput"),
              value: hourlyRate,
              onChange: (v) => {
                setValue("hourlyRate", v);
                handleFieldChange("hourlyRate");
              },
              placeholder: "0.00",
              type: "currency",
            },
          ]}
          computedTotal={computedLaborCost}
          sliderValue={0}
          onSliderChange={() => {}}
          finalTotal={Math.max(0, finalLabor)}
          showSlider={false}
          needsManualInput={laborNeedsManual}
        />
        <DefaultIndicator fieldName="hourlyRate" fieldsFromDefaults={defaults} />
      </Box>

      {/* No-location warning banner — shown above travel section */}
      {!providerHasLocation && <NoLocationBanner />}

      {/* Travel Distance — percentage slider */}
      <Box>
        <PricingLineItem
          title={translate("travelDistance")}
          inputs={[
            {
              label: translate("distanceKm"),
              value: travelDistanceKm,
              onChange: (v) => setValue("travelDistanceKm", v),
              placeholder: "0",
              adornment: "km",
            },
            {
              label: translate("ratePerKm"),
              value: travelRatePerKm,
              onChange: (v) => {
                setValue("travelRatePerKm", v);
                handleFieldChange("travelRatePerKm");
              },
              placeholder: "0.00",
              type: "currency",
            },
          ]}
          computedTotal={computedTravelDistanceCost}
          sliderValue={travelDistanceSlider}
          onSliderChange={(v) => setValue("travelDistanceSliderAdjustment", v)}
          finalTotal={Math.max(0, finalTravelDistance)}
          showSlider={!isDesktop}
          usePercentageSlider
          percentageBaseValue={baseDistanceKm}
          percentageUnit="km"
          titleExtra={changeDepartureLink}
        />
        <DefaultIndicator fieldName="travelRatePerKm" fieldsFromDefaults={defaults} />
      </Box>

      {/* Travel Time — percentage slider */}
      <Box>
        <PricingLineItem
          title={translate("travelTime")}
          inputs={[
            {
              label: translate("travelTimeHoursInput"),
              value: travelTimeHours,
              onChange: (v) => setValue("travelTimeHours", v),
              placeholder: "0",
              adornment: "hrs",
            },
            {
              label: translate("ratePerHour"),
              value: travelRatePerHour,
              onChange: (v) => {
                setValue("travelRatePerHour", v);
                handleFieldChange("travelRatePerHour");
              },
              placeholder: "0.00",
              type: "currency",
            },
          ]}
          computedTotal={computedTravelTimeCost}
          sliderValue={travelTimeSlider}
          onSliderChange={(v) => setValue("travelTimeSliderAdjustment", v)}
          finalTotal={Math.max(0, finalTravelTime)}
          showSlider={!isDesktop}
          usePercentageSlider
          percentageBaseValue={baseTimeHours}
          percentageUnit="hrs"
        />
        <DefaultIndicator fieldName="travelRatePerHour" fieldsFromDefaults={defaults} />
      </Box>

      {/* Parts & Materials — no slider */}
      <PricingLineItem
        title={translate("partsCost")}
        inputs={[
          {
            label: translate("materialCostInput"),
            value: partsCost,
            onChange: (v) => setValue("partsCost", v),
            placeholder: "0.00",
            type: "currency",
          },
        ]}
        sliderValue={0}
        onSliderChange={() => {}}
        finalTotal={Math.max(0, finalMaterial)}
        showSlider={false}
        noteField={{
          value: materialDescription || "",
          onChange: (v) => setValue("materialDescription", v),
          placeholder: translate("materialNotePlaceholder"),
        }}
        needsManualInput={partsNeedsManual}
      />

      {/* Additional Costs — no slider */}
      <PricingLineItem
        title={translate("additionalCosts")}
        inputs={[
          {
            label: translate("additionalCostsInput"),
            value: additionalCosts,
            onChange: (v) => setValue("additionalCosts", v),
            placeholder: "0.00",
            type: "currency",
          },
        ]}
        sliderValue={0}
        onSliderChange={() => {}}
        finalTotal={Math.max(0, finalAdditional)}
        showSlider={false}
        noteField={{
          value: additionalCostsNote || "",
          onChange: (v) => setValue("additionalCostsNote", v),
          placeholder: translate("additionalCostsNotePlaceholder"),
        }}
        needsManualInput={additionalNeedsManual}
      />
    </>
  );

  const pricingSummary = (
    <PricingSummary
      items={[
        { label: translate("laborCost"), value: Math.max(0, finalLabor) },
        { label: translate("travelDistance"), value: Math.max(0, finalTravelDistance) },
        { label: translate("travelTime"), value: Math.max(0, finalTravelTime) },
        { label: translate("partsCost"), value: Math.max(0, finalMaterial) },
        { label: translate("additionalCosts"), value: Math.max(0, finalAdditional) },
      ]}
      total={Math.max(0, grandTotal)}
    />
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Desktop: 2-column layout with sticky summary */}
      {isDesktop ? (
        <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
          {/* Left column: pricing line items */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2.5 }}>
            {pricingLineItems}
          </Box>
          {/* Right column: sticky summary */}
          <Box sx={{ width: 300, flexShrink: 0, position: "sticky", top: 100, alignSelf: "flex-start" }}>
            {pricingSummary}
          </Box>
        </Box>
      ) : (
        /* Mobile/Tablet: single column */
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {pricingLineItems}
          {pricingSummary}
        </Box>
      )}
    </Box>
  );
};

export default StepPricing;
